import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Loader2 } from 'lucide-react'

interface SendEmailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  recipientName: string
  recipientCount?: number
  isBulk?: boolean
  onSend: (data: { subject: string; htmlContent: string; textContent: string }) => Promise<void>
  isLoading?: boolean
}

export function SendEmailDialog({
  open,
  onOpenChange,
  recipientName,
  recipientCount,
  isBulk = false,
  onSend,
  isLoading,
}: SendEmailDialogProps) {
  const [subject, setSubject] = useState('')
  const [content, setContent] = useState('')

  const handleSend = async () => {
    await onSend({
      subject,
      htmlContent: `<div style="font-family: Arial, sans-serif; padding: 20px;">${content.replace(/\n/g, '<br/>')}</div>`,
      textContent: content,
    })
    setSubject('')
    setContent('')
    onOpenChange(false)
  }

  const title = isBulk
    ? `Send Email to ${recipientCount} Artists`
    : `Send Email to ${recipientName}`

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {isBulk
              ? 'Compose an email to send to all selected artists. Use {{firstName}} for personalization.'
              : 'Compose a direct email to this artist.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">Message</Label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your message here..."
              rows={10}
            />
            {isBulk && (
              <p className="text-xs text-muted-foreground">
                Available variables: {'{{firstName}}'}, {'{{artisticName}}'}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={!subject || !content || isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
